import { notify } from "~/components/notifications";
import { NotificationType } from "~/shared/enums";
import type { SpeechToTextFormat } from "~/shared/types";
import { usePageStore } from "~/stores";

const SPEECH_TO_TEXT_MIME_FORMATS: SpeechToTextFormat[] = [
  { recorderMimeType: "audio/webm", uploadMimeType: "audio/webm", extension: "webm" },
  { recorderMimeType: "audio/mp4;codecs=mp4a.40.2", uploadMimeType: "audio/mp4", extension: "mp4" }, // For Safari 15+ support
  { recorderMimeType: "audio/webm;codecs=opus", uploadMimeType: "audio/webm", extension: "webm" }, // For old Chrome support
  { recorderMimeType: "audio/mp3", uploadMimeType: "audio/mp3", extension: "mp3" },
  { recorderMimeType: "audio/wav", uploadMimeType: "audio/wav", extension: "wav" },
];

export const SHORT_RECORDING_ERROR_MESSAGE = "Recording too short to transcribe";

export const ensureRecorderFormat = () => {
  if (typeof window.MediaRecorder === "undefined") {
    return null;
  }

  // Try formats in priority order: WebM (most common), then MP4/M4A, then others
  return SPEECH_TO_TEXT_MIME_FORMATS.find((format) => MediaRecorder.isTypeSupported(format.recorderMimeType)) ?? null;
};

export const notifyUnsupportedRecording = () => {
  notify({
    message: "Recording is not supported in this browser, please consider upgrading your browser",
    type: NotificationType.ERROR,
  });
};

export const notifyGenericFailedRecording = () => {
  notify({
    message: "Failed to start recording, please try again or contact support",
    type: NotificationType.ERROR,
  });
};

export const notifyGenericTranscriptionFailed = () => {
  notify({
    message: "Speech to text transcription failed, please contact support",
    type: NotificationType.ERROR,
  });
};

export const notifyShortRecordingWarning = () => {
  notify({
    message: SHORT_RECORDING_ERROR_MESSAGE,
    type: NotificationType.WARNING,
  });
};

type PermissionLink = {
  label: string;
  url: string;
};

type NotificationAction = {
  label: string;
  link: PermissionLink;
};

const OS_PERMISSION_LINKS: Record<string, PermissionLink> = {
  mac: {
    label: "macOS microphone settings",
    url: "https://support.apple.com/guide/mac-help/control-access-to-the-microphone-on-mac-mchla1b1e1fe/mac",
  },
  windows: {
    label: "Windows microphone privacy",
    url: "https://support.microsoft.com/en-us/windows/turn-on-app-permissions-for-your-microphone-in-windows-94991183-f69d-b4cf-4679-c98ca45f577a",
  },
  ios: {
    label: "iOS microphone permissions",
    url: "https://discussions.apple.com/thread/251593487?sortBy=rank",
  },
  android: {
    label: "Android microphone permissions",
    url: "https://support.google.com/android/answer/9431959?hl=en",
  },
};

const BROWSER_PERMISSION_LINKS: Record<string, PermissionLink> = {
  chrome: {
    label: "Chrome microphone permissions",
    url: "https://support.google.com/chrome/answer/114662",
  },
  safari: {
    label: "Safari website permissions",
    url: "https://support.apple.com/guide/safari/websites-ibrwe2159f50/mac",
  },
  firefox: {
    label: "Firefox microphone permissions",
    url: "https://support.mozilla.org/en-US/kb/how-manage-your-camera-and-microphone-permissions",
  },
  edge: {
    label: "Edge microphone permissions",
    url: "https://learn.microsoft.com/en-us/answers/questions/2374559/microsoft-edge-microphone",
  },
};

const resolveOsPermissionLink = (pageStore: ReturnType<typeof usePageStore>): PermissionLink | null => {
  if (pageStore.isIos) {
    return OS_PERMISSION_LINKS.ios;
  }
  if (pageStore.isAndroid) {
    return OS_PERMISSION_LINKS.android;
  }
  if (pageStore.isMac) {
    return OS_PERMISSION_LINKS.mac;
  }
  if (pageStore.isWindows) {
    return OS_PERMISSION_LINKS.windows;
  }
  return null;
};

const resolveBrowserPermissionLink = (pageStore: ReturnType<typeof usePageStore>): PermissionLink | null => {
  if (pageStore.isSafari) {
    return BROWSER_PERMISSION_LINKS.safari;
  }
  if (pageStore.isChrome) {
    return BROWSER_PERMISSION_LINKS.chrome;
  }
  if (pageStore.isFirefox) {
    return BROWSER_PERMISSION_LINKS.firefox;
  }
  if (pageStore.isEdge) {
    return BROWSER_PERMISSION_LINKS.edge;
  }
  return null;
};

const buildPermissionActions = (actions: NotificationAction[]) =>
  actions.map(({ label, link }) => ({
    label,
    onClick: (dismissFn: () => void) => {
      window.open(link.url, "_blank", "noopener,noreferrer");
      dismissFn();
    },
  }));

export const notifyNotFoundRecordingError = () => {
  const osLink = resolveOsPermissionLink(usePageStore());
  notify({
    message: "Microphone not found, please check microphone permissions on your operating system",
    type: NotificationType.ERROR,
    actions: osLink ? buildPermissionActions([{ label: "Open OS settings", link: osLink }]) : undefined,
  });
};

export const notifyNotAllowedRecordingError = () => {
  const pageStore = usePageStore();
  const osLink = resolveOsPermissionLink(pageStore);
  const browserLink = resolveBrowserPermissionLink(pageStore);
  notify({
    message: `Failed to start recording, please check your microphone permissions in both the ${pageStore.isMobile || pageStore.isDesktop ? "app" : "browser"} and your operating system`,
    type: NotificationType.ERROR,
    actions:
      osLink && browserLink
        ? buildPermissionActions([
            { label: "Device help", link: osLink },
            { label: "Browser help", link: browserLink },
          ])
        : undefined,
  });
};
